/**
 * Typed event catalog for first-party analytics (cue-forge-do7.1). This is the
 * honesty guarantee: every v1 event and every prop value is a closed enum or a
 * bucket label, so free text, filenames, cue content, URLs, and license keys are
 * UNREPRESENTABLE in the types. The only non-enum string that ever flows is a
 * same-origin `RefPath`, produced solely by {@link refPathFrom}, and a validated
 * `LangTag` from {@link langTagFrom}.
 *
 * `src/app/analytics/**` must not import the core barrel (it is on the eager,
 * budget-measured bundle), so the closed sets below are hand-listed and mirror
 * their core sources; keep them in sync.
 */

/** Mirrors the format ids in `src/core/formats/registry` (do not import core). */
export type FormatId =
  | 'srt'
  | 'vtt'
  | 'sbv'
  | 'ass'
  | 'ttml'
  | 'scc'
  | 'stl'
  | 'itt'
  | 'mcc'
  | 'ebuttd'
  | 'imsc101'
  | 'imsc11';

/** Mirrors `RuleId` in `src/core/qc/types.ts` (do not import core). */
export type RuleId =
  | 'max-chars-per-line'
  | 'max-lines-per-cue'
  | 'max-cps'
  | 'min-duration'
  | 'max-duration'
  | 'min-gap'
  | 'overlap'
  | 'reversed-timing'
  | 'empty-cue'
  | 'duplicate-cue'
  | 'forbidden-characters'
  | 'punctuation-spacing'
  | 'unclosed-tag'
  | 'cue-count-monotonic-timing'
  | 'bidi-punctuation'
  | 'mixed-digit-style'
  | 'latin-run-isolation'
  | 'arabic-line-length'
  | 'kashida-strip'
  | 'ellipsis-style';

/**
 * The built-in QC presets plus the literal `'custom'`. Custom preset ids/names
 * are NEVER sent — they can carry user free text — so every custom preset
 * collapses to `'custom'`. Mirrors `PresetName` in `src/core/qc/presets`.
 */
export type PresetId = 'generic' | 'netflix-latin' | 'youtube' | 'netflix-arabic' | 'custom';

/** How an import failed. Small closed set — never the parser's prose message. */
export type ImportErrorKind = 'parse' | 'unsupported' | 'empty' | 'read' | 'other';

/** Mirrors `TranscribeErrorKind` in `src/app/transcribe/types.ts`. */
export type TranscribeErrorKind = 'decode' | 'model-load' | 'transcribe' | 'other';

/** The two app modes. */
export type Mode = 'studio' | 'convert';

/** Where a Pro gate / upsell was shown or clicked. Closed set of gated surfaces. */
export type Surface =
  | 'convert'
  | 'batch'
  | 'video'
  | 'timing'
  | 'transcribe'
  | 'export'
  | 'preset-editor'
  | 'qc-export'
  // A Pro-format import gate (a free user opening SCC/STL/TTML/…). Rendered from
  // both the Convert dropzone and the Studio EmptyState — one surface for both.
  | 'import';

// --- Bucket label types (raw counts/durations never leave the client) ---------

export type CueCountBucket = '1-10' | '11-100' | '101-1000' | '1000+';
/** Issue / warning / failure counts. */
export type CountBucket = '0' | '1-10' | '11-100' | '100+';
export type DurationBucket = '<1m' | '1-5m' | '5-20m' | '20m+';
export type FileCountBucket = '2-5' | '6-20' | '21+';

/** Number of a cue count into its coarse bucket (0 and negatives clamp low). */
export function bucketCueCount(n: number): CueCountBucket {
  const v = Number.isFinite(n) ? n : 0;
  if (v <= 10) return '1-10';
  if (v <= 100) return '11-100';
  if (v <= 1000) return '101-1000';
  return '1000+';
}

/** Coarse issue/warning/failure bucket (negatives and non-finite clamp to '0'). */
export function bucketCount(n: number): CountBucket {
  const v = Number.isFinite(n) ? n : 0;
  if (v <= 0) return '0';
  if (v <= 10) return '1-10';
  if (v <= 100) return '11-100';
  return '100+';
}

/** Coarse media-duration bucket. Input is seconds. */
export function bucketDuration(seconds: number): DurationBucket {
  const v = Number.isFinite(seconds) ? seconds : 0;
  if (v < 60) return '<1m';
  if (v < 300) return '1-5m';
  if (v < 1200) return '5-20m';
  return '20m+';
}

/** Coarse batch file-count bucket (batches are always >= 2 files). */
export function bucketFileCount(n: number): FileCountBucket {
  const v = Number.isFinite(n) ? n : 0;
  if (v <= 5) return '2-5';
  if (v <= 20) return '6-20';
  return '21+';
}

// --- RefPath: the one same-origin, validated string that may flow ------------

declare const refPathBrand: unique symbol;
/** A same-origin pathname (<=64 chars) produced only by {@link refPathFrom}. */
export type RefPath = string & { readonly [refPathBrand]: true };

/**
 * Returns the referrer's pathname (query and hash stripped, truncated to 64
 * chars) ONLY when the referrer is same-origin as `origin`. Cross-origin,
 * empty, or malformed inputs return undefined. Never the full URL, never a
 * cross-origin value.
 */
export function refPathFrom(referrer: string, origin: string): RefPath | undefined {
  if (!referrer) return undefined;
  let ref: URL;
  let base: URL;
  try {
    ref = new URL(referrer);
    base = new URL(origin);
  } catch {
    return undefined;
  }
  if (ref.origin !== base.origin) return undefined;
  return ref.pathname.slice(0, 64) as RefPath;
}

// --- LangTag: a validated lowercase Whisper language name --------------------

declare const langTagBrand: unique symbol;
/** A lowercase Whisper language name (e.g. 'arabic'), from {@link langTagFrom}. */
export type LangTag = string & { readonly [langTagBrand]: true };

/**
 * Validates a Whisper language name. Whisper names are single lowercase ASCII
 * words; anything else (codes, mixed case, spaces, free text) returns undefined.
 */
export function langTagFrom(name: string): LangTag | undefined {
  if (!/^[a-z]{2,32}$/.test(name)) return undefined;
  return name as LangTag;
}

// --- The event catalog -------------------------------------------------------

/**
 * Every v1 event mapped to its allowed props. Empty-prop events use
 * `Record<string, never>` so call sites must pass `{}` and nothing more.
 */
export interface EventProps {
  app_open: { mode: Mode; pro: 'yes' | 'no'; ref?: RefPath; to?: FormatId };
  mode_switch: { to: Mode };
  import: {
    // Optional: a failed import (unknown format, read error) has no known
    // source format — the ok path always supplies it.
    format?: FormatId;
    outcome: 'ok' | 'fail';
    error?: ImportErrorKind;
    cues?: CueCountBucket;
    warnings?: CountBucket;
  };
  export: { format: FormatId; surface: 'studio' | 'convert' };
  qc_run: { preset: PresetId; issues: CountBucket };
  // `rule` is optional: a per-issue fix (scope 'one') carries its single rule,
  // but fix-all spans every fixable rule at once and has no single rule id.
  qc_fix: { rule?: RuleId; scope: 'one' | 'all'; result: 'fixed' | 'unfixable' };
  transcribe: {
    stage: 'start' | 'done' | 'error';
    error?: TranscribeErrorKind;
    language?: LangTag;
    duration?: DurationBucket;
  };
  batch_run: { files: FileCountBucket; failures: CountBucket };
  timing_tool: { tool: 'shift' | 'linear' | 'framerate' | 'drift' | 'resync' };
  video_load: Record<string, never>;
  advanced_edit_open: Record<string, never>;
  custom_preset_created: Record<string, never>;
  pro_gate_view: { surface: Surface };
  upsell_click: { surface: Surface };
  trial_click: Record<string, never>;
  license_activate: { outcome: 'ok' | 'invalid' | 'error' };
  error_boundary: Record<string, never>;
}

export type EventName = keyof EventProps;

/**
 * All 17 v1 event names as a plain string array. A wave-2 drift-guard test
 * asserts the ingest worker's allowlist equals this list, so keep it a plain
 * array (not derived from a type at runtime).
 */
export const EVENT_NAMES: readonly EventName[] = [
  'app_open',
  'mode_switch',
  'import',
  'export',
  'qc_run',
  'qc_fix',
  'transcribe',
  'batch_run',
  'timing_tool',
  'video_load',
  'advanced_edit_open',
  'custom_preset_created',
  'pro_gate_view',
  'upsell_click',
  'trial_click',
  'license_activate',
  'error_boundary',
];
